import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { describeError } from "@/lib/api";
import { Lightning, Eye, EyeSlash } from "@phosphor-icons/react";

export default function Login() {
  const { login } = useAuth();
  const [email, setEmail] = useState(() => localStorage.getItem("luminosa_email") || "admin@luminosa.local");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  // Focus password if email is pre-filled
  useEffect(() => {
    if (email) {
      const pwdInput = document.querySelector('[data-testid="login-password-input"]');
      if (pwdInput) pwdInput.focus();
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center relative overflow-hidden bg-[#0A0A0A]">
      <div
        className="absolute inset-0 bg-cover bg-center opacity-30"
        style={{
          backgroundImage:
            "url(https://images.unsplash.com/photo-1673266893352-6de89e258064?crop=entropy&cs=srgb&fm=jpg&w=2000&q=70)",
        }}
      />
      <div className="absolute inset-0 bg-gradient-to-br from-black via-black/90 to-black/70" />
      <div className="absolute inset-0 dotted-bg opacity-30" />

      <div className="relative z-10 w-full max-w-md mx-4">
        <div className="mb-10 flex items-center gap-3">
          <div className="w-12 h-12 rounded bg-[#CCFF00] flex items-center justify-center">
            <Lightning size={28} weight="fill" className="text-black" />
          </div>
          <div>
          <div className="text-xs text-zinc-500 tracking-[0.3em] uppercase font-mono">CLUB</div>
            <h1 className="font-display font-black text-3xl tracking-tight leading-none">LUCES DON TELLO</h1>
          </div>
        </div>

        <div className="bg-[#121212]/80 backdrop-blur-md border border-zinc-800 rounded-md p-8">
          <h2 className="font-display text-2xl font-bold mb-2">Iniciar sesión</h2>
          <p className="text-sm text-zinc-400 mb-6">Panel de control de automatización de luces</p>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs text-zinc-500 tracking-wider uppercase font-semibold mb-2">
                Email
              </label>
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                autoComplete="username"
                className="w-full bg-black border border-zinc-800 rounded px-3 py-2.5 text-white focus:border-[#CCFF00] focus:outline-none focus:ring-1 focus:ring-[#CCFF00] transition-colors"
              />
            </div>
            <div>
              <label className="block text-xs text-zinc-500 tracking-wider uppercase font-semibold mb-2">
                Contraseña
              </label>
              <div className="relative">
                <input
                  data-testid="login-password-input"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  autoComplete="current-password"
                  className="w-full bg-black border border-zinc-800 rounded px-3 py-2.5 pr-10 text-white focus:border-[#CCFF00] focus:outline-none focus:ring-1 focus:ring-[#CCFF00] transition-colors"
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-1 text-zinc-500 hover:text-zinc-300"
                  aria-label={showPassword ? "Ocultar contraseña" : "Mostrar contraseña"}
                >
                  {showPassword ? <EyeSlash size={18} weight="bold" /> : <Eye size={18} weight="bold" />}
                </button>
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-950/30 border border-red-900/50 rounded px-3 py-2">
                {error}
              </div>
            )}
            <button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full bg-[#CCFF00] text-black font-bold py-3 rounded hover:bg-[#B3E600] active:scale-[0.98] transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? "Accediendo..." : "Acceder"}
            </button>
          </form>
        </div>

        <div className="mt-6 text-center text-xs text-zinc-600 font-mono tracking-wider">
          v1.0 · Mérida
        </div>
      </div>
    </div>
  );
}
